import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { EtatExpedition } from '../components/notifications/BandeauExpedition';

export type CanalNotification = 'IN_APP' | 'EMAIL' | 'SMS' | 'PUSH';

export interface CanalDeLEnvoi {
  canal: CanalNotification;
  libelle: string;
  ton: string;
}

/**
 * Un envoi (TF-462) : parti par l'application, le mail ou le push, c'est une
 * ligne, ses canaux en pastilles ; « marquer lu » le désigne par son `id`.
 */
export interface LigneNotification {
  id: string;
  titre: string;
  corps: string;
  canaux: CanalDeLEnvoi[];
  lu: boolean;
  envoyeeLe: string;
  lueLe: string | null;
  regle: string | null;
  apprenantId: string | null;
  apprenant: string | null;
  /** Facture en retard de paiement : sa fiche s'ouvre dans « Factures » (TF-422). */
  factureId: string | null;
  /** Questionnaire noté sous 3/5 : la réponse s'ouvre dans « Satisfaction » (TF-434). */
  reponseId: string | null;
  /** Formateur au-delà de son seuil de requêtes IA : sa ligne s'ouvre dans « Supervision IA » (TF-439). */
  formateurIaId: string | null;
  /** Question de la discussion d'un module : elle s'ouvre dans le lecteur, depuis cette leçon (TF-448). */
  discussion: { leconId: string; questionId: string } | null;
}

export interface ReponseNotifications {
  notifications: LigneNotification[];
  /** En envois. */
  compteurs: { total: number; nonLues: number; mails: number; sms: number };
  expedition: EtatExpedition;
}

export function useNotifications(filtres: { canal?: CanalNotification; nonLues?: boolean }) {
  const parametres = new URLSearchParams();
  if (filtres.canal) parametres.set('canal', filtres.canal);
  if (filtres.nonLues) parametres.set('nonLues', 'true');

  return useQuery({
    queryKey: ['notifications', parametres.toString()],
    queryFn: () => appelApi<ReponseNotifications>(`/notifications?${parametres.toString()}`),
  });
}

export function useMarquerLues() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (identifiants?: string[]) =>
      appelApi<{ marquees: number }>('/notifications/lues', {
        methode: 'PATCH',
        corps: identifiants ? { identifiants } : {},
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
